/**
 * HubTooltip — the small carbon note that appears after a short pause over a control
 * or a cut-off label (Design System 2.0, step 5 wave 1b; the published page `HubTooltip`).
 *
 * What it says: the name of an icon-only button (C22), why a disabled control is blocked
 * and what unblocks it (C16, C22), the full words of a label cut with "…" (D-038), the
 * word a squeezed button dropped (D-038), or one line of "how we know" a number (B19).
 * Never anything a person must read to finish the task, and never a button, link or form
 * (that is a menu or a popover).
 *
 * - `--carbon` with `--on-carbon` words, Inter 12.5 / 1.45, 4px corners, the pop shadow,
 *   at most 260 wide — longer words wrap inside it (D-017 B14, D-010).
 * - An optional bold `lead` in Inter Tight 700 ("Measured · from the time tracker").
 * - Appears after the pointer rests 400ms — also when moving from one tooltip straight to
 *   the next, so it never shows with no delay on hover (C22). Hides when the pointer
 *   leaves, or on Esc (C19).
 *
 * A thin wrapper over MUI `Tooltip`, so it inherits MUI's portal, positioning, focus and
 * touch handling; the app theme (`theme.ts` `baseTooltip`) already draws MUI tooltips this
 * way, and the wrapper carries the same values itself so it looks the same under any theme.
 * The words are required (a plain string), so the thing it points at always has an
 * accessible name (`describeChild` makes them its description instead — use that when the
 * child already has a name, e.g. a cut title or a disabled button that keeps its label).
 *
 * A disabled `<button disabled>` gets no pointer events: give the control `aria-disabled`
 * instead (as `HubCheckbox` / `HubSwitch` do), or wrap it in a `<span>`.
 */
import React from 'react';
import { Tooltip } from '@mui/material';
import type { TooltipProps } from '@mui/material';
import { devWarn } from './devWarn';

/** C22 — every tooltip waits this long. */
export const HUB_TOOLTIP_DELAY = 400;

export type HubTooltipProps = Omit<TooltipProps, 'title' | 'children' | 'enterDelay' | 'enterNextDelay'> & {
  /** The words: one short line, at most two. Required — it is the child's accessible name (or, with `describeChild`, its description). */
  title: string;
  /** An optional bold lead before the words, in Inter Tight 700. */
  lead?: string;
  /** The one element it explains (it must hold a ref and take pointer / focus events). */
  children: React.ReactElement;
};

/* bundle.css `.ds-tip` (= theme.ts baseTooltip): carbon, 12.5 / 1.45, 6px 9px, 4px, ≤260, the pop shadow. */
const TIP_SX = {
  backgroundColor: 'var(--carbon)',
  color: 'var(--on-carbon)',
  fontFamily: 'var(--font-body)',
  fontSize: '12.5px',
  fontWeight: 400,
  lineHeight: 1.45,
  padding: '6px 9px',
  borderRadius: '4px',
  maxWidth: 260,
  boxShadow: 'var(--pop)',
  '& b': { fontFamily: 'var(--font-head)', fontWeight: 700 },
} as const;
const ARROW_SX = { color: 'var(--carbon)' } as const;

export const HubTooltip = React.forwardRef<HTMLDivElement, HubTooltipProps>(function HubTooltip(
  { title, lead, children, arrow = true, placement = 'top', describeChild, slotProps, ...rest },
  ref,
) {
  const words = (title || '').trim();
  if (!words) devWarn('HubTooltip:no-title', 'HubTooltip needs its words: an empty title shows nothing and leaves the control unnamed.');
  const plain = lead ? `${lead} · ${words}` : words;
  const content: React.ReactNode = lead ? <><b>{lead}</b> · {words}</> : words;

  /* MUI names the child from a STRING title only; with a lead the title is a node, so
     name the child with the same words in plain text (its own aria-label still wins). */
  const childProps = children.props as { 'aria-label'?: string };
  const child = lead && !describeChild && words && childProps['aria-label'] == null
    ? React.cloneElement(children as React.ReactElement<{ 'aria-label'?: string }>, { 'aria-label': plain })
    : children;

  return (
    <Tooltip
      ref={ref}
      title={words ? content : ''}
      enterDelay={HUB_TOOLTIP_DELAY}
      enterNextDelay={HUB_TOOLTIP_DELAY}
      arrow={arrow}
      placement={placement}
      describeChild={describeChild}
      slotProps={{
        ...slotProps,
        tooltip: { ...slotProps?.tooltip, sx: TIP_SX },
        arrow: { ...slotProps?.arrow, sx: ARROW_SX },
      }}
      {...rest}
    >
      {child}
    </Tooltip>
  );
});

export default HubTooltip;
